import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { endOfMonth, format, isSameMonth, startOfMonth } from 'date-fns';
import { CalendarMonth } from '../../models/calendar-month';
import { IDateRange } from '../../models/date-range';

@Component({
    selector: 'calendar-months-grid',
    template: `
        <div class="calendar-grid">
            <ng-container *ngFor="let month of months; trackBy: monthTrackByFn">
                <calendar-month
                    [date]="month.date"
                    [disabled]="getIsDisabled(month)"
                    [isStartOfRange]="getIsStartOfRange(month)"
                    [isEndOfRange]="getIsEndOfRange(month)"
                    [withinRange]="getIsWithinRange(month)"
                    [isToday]="getIsToday(month)"
                    [selected]="getIsSelected(month)"
                    (monthClick)="handleMonthClick(month)"
                >
                </calendar-month>
            </ng-container>
        </div>
    `,
    styleUrls: ['./calendar-months-grid.component.scss']
})
export class CalendarMonthsGridComponent implements OnInit, OnChanges {
    @Input() public date: Date;
    @Input() public dateRange: IDateRange;
    @Input() public year: Date;
    @Input() public lowerLimit: Date;
    @Input() public upperLimit: Date;
    @Output() public dateChange = new EventEmitter<Date>();

    public months: CalendarMonth[] = [];
    public format = format;
    private _now = new Date();

    // Cache.
    private _isTodayCache = new Map<CalendarMonth, boolean>();
    private _isSelectedCache = new Map<CalendarMonth, boolean>();
    private _isStartOfRangeCache = new Map<CalendarMonth, boolean>();
    private _isEndOfRangeCache = new Map<CalendarMonth, boolean>();
    private _isWithinRangeCache = new Map<CalendarMonth, boolean>();
    private _isHiddenCache = new Map<CalendarMonth, boolean>();
    private _isMutedCache = new Map<CalendarMonth, boolean>();
    private _isDisabledCache = new Map<CalendarMonth, boolean>();

    // Lifecycle hooks.
    public ngOnInit(): void {
        this.updateMonths();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            !!changes.year ||
            // This would mean that the DatePickerType has changed.
            (!!changes.date && !!changes.dateRange)
        ) {
            this.updateMonths();
        }

        // If something important changes, clear the cache.
        if (!!changes.year || !!changes.date || !!changes.dateRange) {
            this._isTodayCache.clear();
            this._isSelectedCache.clear();
            this._isStartOfRangeCache.clear();
            this._isEndOfRangeCache.clear();
            this._isWithinRangeCache.clear();
            this._isHiddenCache.clear();
            this._isMutedCache.clear();
            this._isDisabledCache.clear();
        }
    }

    public updateMonths(): void {
        const year = this.year || this._now;

        for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
            let month: CalendarMonth;
            if (!this.months[monthIndex]) {
                month = new CalendarMonth();
                this.months.push(month);
            } else {
                month = this.months[monthIndex];
            }

            month.date = new Date(year.getFullYear(), monthIndex, 1);
        }
    }

    // Accessors for boolean inputs.
    public getIsToday(month: CalendarMonth): boolean {
        if (!this._isTodayCache.has(month)) {
            this._isTodayCache.set(month, isSameMonth(this._now, month.date));
        }
        return this._isTodayCache.get(month);
    }

    public getIsSelected(month: CalendarMonth): boolean {
        if (!this._isSelectedCache.has(month)) {
            if (!!this.date) {
                this._isSelectedCache.set(month, isSameMonth(this.date, month.date));
            } else if (!!this.dateRange && !!this.dateRange.start) {
                this._isSelectedCache.set(
                    month,
                    isSameMonth(this.dateRange.start, month.date) ||
                        isSameMonth(this.dateRange.end, month.date)
                );
            } else {
                this._isSelectedCache.set(month, false);
            }
        }
        return this._isSelectedCache.get(month);
    }

    public getIsStartOfRange(month: CalendarMonth): boolean {
        if (!this.dateRange) {
            return false;
        }
        if (!this._isStartOfRangeCache.has(month)) {
            this._isStartOfRangeCache.set(month, isSameMonth(this.dateRange.start, month.date));
        }
        return this._isStartOfRangeCache.get(month);
    }

    public getIsEndOfRange(month: CalendarMonth): boolean {
        if (!this.dateRange) {
            return false;
        }
        if (!this._isEndOfRangeCache.has(month)) {
            this._isEndOfRangeCache.set(
                month,
                (!this.dateRange.end && this.getIsStartOfRange(month)) ||
                    isSameMonth(this.dateRange.end, month.date)
            );
        }
        return this._isEndOfRangeCache.get(month);
    }

    public getIsWithinRange(month: CalendarMonth): boolean {
        if (!this.dateRange) {
            return false;
        }
        if (!this._isWithinRangeCache.has(month)) {
            this._isWithinRangeCache.set(
                month,
                startOfMonth(month.date) > this.dateRange.start &&
                    endOfMonth(month.date) < this.dateRange.end
            );
        }
        return this._isWithinRangeCache.get(month);
    }

    public getIsDisabled(month: CalendarMonth): boolean {
        if (!this._isDisabledCache.has(month)) {
            this._isDisabledCache.set(
                month,
                (!!this.lowerLimit && month.date < this.lowerLimit) ||
                    (!!this.upperLimit && month.date > this.upperLimit)
            );
        }
        return this._isDisabledCache.get(month);
    }

    // Event handlers.
    public handleMonthClick(month: CalendarMonth): void {
        if (month.disabled) {
            return;
        }
        this.dateChange.emit(month.date);
    }

    // Template optimization.
    public monthTrackByFn(index: number, month: CalendarMonth): Date {
        return month.date;
    }
}
